import { v4 as uuid } from 'uuid';

import {
	participantColors,
	getParticipantColor,
	type ParticipantColor,
} from '@atlaskit/editor-shared-styles';
import { token } from '@atlaskit/tokens';
import type { PresenceActivity } from '@atlaskit/editor-common/collab';

import type { PresenceParticipant, PresenceSessionData } from './presence-store/index.ts';
import type { PresenceCanvasContentType, PresenceContentType } from './presenceTypes.ts';

declare global {
	interface Window {
		__backendOriginHost: string | undefined;
	}
}

const presencePrefix = '/ncs-presence';

/**
 * Retrieves the border color associated with a given string ID.
 *
 * @param id - The ID for which to get the border color. If undefined, a default color is returned.
 * @returns ParticipantColor object with text color and background color
 */
export const getParticipantColors = (id: string | undefined): ParticipantColor => {
	if (!id) {
		return participantColors[0];
	}

	return getParticipantColor(id).color;
};

/**
 * Retrieves the border color of the current user, which should always be blue.
 *
 * @returns ParticipantColor object with text color and background color
 */
export const getCurrentUserColors = (): ParticipantColor => {
	return {
		textColor: token('color.text.inverse'),
		backgroundColor: token('color.background.accent.blue.bolder'),
		svgBackgroundColor: token('color.background.accent.blue.subtler'),
	};
};

export const getCurrentUserFromMap = (
	participants: Map<string, PresenceParticipant>,
	sessionId: string | undefined,
) => {
	if (sessionId) {
		return [...participants.values()].find((p) => p.sessions[sessionId]);
	}
};

export const getActivityToDisplay = (
	sessionData: PresenceSessionData,
	currentUserSessionId?: string,
	defaultActivity?: PresenceActivity,
) => {
	if (currentUserSessionId) {
		return sessionData[currentUserSessionId]?.activity ?? defaultActivity ?? 'viewer';
	} else {
		const hasEditor = Object.keys(sessionData).find(
			(sessionId) => sessionData[sessionId]?.activity === 'editor',
		);
		return hasEditor ? 'editor' : 'viewer';
	}
};

/**
 * Gets the URL of the presence service.
 *
 * @returns {string} The URL of the presence service.
 */
export const getPresenceServiceUrl = (): string => {
	const route = 'collab-presence-confluence';

	if (window.location.hostname === 'localhost' && process.env.PILLAR_LOCAL_DEVELOPMENT !== 'true') {
		const host = window.__backendOriginHost;

		return `https://${host}/${route}`;
	}

	return `${window.location.origin}/${route}`;
};

/**
 * Gets the sharded routing path to call presence service
 *
 * @returns {string} The sharded routing path
 */
export const getPresenceServicePath = (cloudId: string, activationId: string): string => {
	return `${presencePrefix}/${cloudId}/${activationId}/confluence`;
};

const presenceCanvasContentTypes: PresenceCanvasContentType[] = ['database', 'whiteboard', 'slide'];

export const isCanvasContentType = (
	contentType: PresenceContentType,
): contentType is PresenceCanvasContentType =>
	presenceCanvasContentTypes.includes(contentType as PresenceCanvasContentType);
/**
 * Generates a unique presence client ID.
 *
 * @returns {string} A string representing the unique presence client ID, prefixed with 'presence-'.
 */
export const getPresenceClientId = (): string => `presence-${uuid()}`;

// the max number for the initial group of avatars before displaying a popover menu
export const MAX_GROUP_SIZE = 4;
export const CURRENT_USER_DUMMY_SESSION_ID = 'dummy-session-id';
export const UNIDENTIFIED = 'unidentified';
